import { useContext, useEffect, useState } from "react";
import Button from "carbon-react/esm/components/button/__next__";
import { FlatTable, FlatTableBody, FlatTableCell, FlatTableHead, FlatTableHeader, FlatTableRow } from "carbon-react/esm/components/flat-table";
import Loader from "carbon-react/esm/components/loader/__next__";
import Message from "carbon-react/esm/components/message";
import Textbox from "carbon-react/esm/components/textbox";

import { getReconciliationView, performReconciliationAction, type ReconciliationRun } from "../api/reconciliationApi";
import { EmbeddedSessionContext } from "../auth/EmbeddedSessionProvider";
import { DateValue, FinancialAmount } from "../components/FinancialValue";
import { MetricCard } from "../components/MetricCard";
import { StatusPill } from "../components/StatusPill";
import { usePaginatedRows } from "../components/TablePagination";

interface ReconciliationSummary {
  clearing_variance?: string;
  exception_loans?: number;
  failed_postings?: number;
  interest_variance?: string;
  oldest_exception?: string | null;
  principal_variance?: string;
  reconciled_loans?: number;
  total_loans?: number;
  unposted_events?: number;
}

export function ReconciliationPage({ onNavigate }: { onNavigate(path: string): void }) {
  const { api } = useContext(EmbeddedSessionContext);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState<{ runs: ReconciliationRun[]; summary: ReconciliationSummary } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const paginatedRuns = usePaginatedRows(data?.runs ?? []);
  const load = () => {
    if (api) void getReconciliationView<{ runs: ReconciliationRun[]; summary: ReconciliationSummary }>(api, "runs").then(setData).catch(() => setError("Reconciliation data could not be loaded."));
  };
  useEffect(load, [api]);
  if (!data) return <Loader loaderLabel="Loading reconciliation" size="medium" />;

  const run = async () => {
    if (!api) return;
    setWorking(true);
    setError(null);
    try {
      const result = await performReconciliationAction<{ run: { id: string } }>(api, { action: "run_reconciliation", asOfDate });
      onNavigate(`/reconciliation/${result.run.id}`);
    } catch {
      setError("The as-of reconciliation could not be completed.");
    } finally {
      setWorking(false);
    }
  };

  const summary = data.summary;
  return <div className="operational-stack">
    {error ? <Message open title="Reconciliation failed" variant="error">{error}</Message> : null}
    <section className="metric-grid">
      <MetricCard icon="tick" label="Reconciled loans" primary>{summary.reconciled_loans ?? 0} / {summary.total_loans ?? 0}</MetricCard>
      <MetricCard icon="warning" label="Exception loans">{summary.exception_loans ?? 0}</MetricCard>
      <MetricCard icon="cash" label="Principal variance"><FinancialAmount currency="USD" value={summary.principal_variance ?? "0"} /></MetricCard>
      <MetricCard icon="chart_line" label="Interest variance"><FinancialAmount currency="USD" value={summary.interest_variance ?? "0"} /></MetricCard>
      <MetricCard icon="bank" label="Clearing variance"><FinancialAmount currency="USD" value={summary.clearing_variance ?? "0"} /></MetricCard>
      <MetricCard icon="alert_on" label="Unposted / failed">{summary.unposted_events ?? 0} / {summary.failed_postings ?? 0}</MetricCard>
    </section>
    <section className="editor-panel">
      <div className="section-heading-row"><div><h2>Run as-of reconciliation</h2><p>Compare active loan schedules and successful Sage journal lines at one reporting date.</p></div><Button disabled={working} onClick={() => void run()} variant="default" variantType="primary">Run reconciliation</Button></div>
      <Textbox label="As-of date" onChange={(event) => setAsOfDate(event.target.value)} value={asOfDate} />
    </section>
    <section className="operational-section"><h2>Run history</h2><FlatTable caption="Reconciliation runs" colorTheme="light" overflowX="auto"><FlatTableHead><FlatTableRow><FlatTableHeader>As of</FlatTableHeader><FlatTableHeader>Created</FlatTableHeader><FlatTableHeader>Items</FlatTableHeader><FlatTableHeader>Exceptions</FlatTableHeader><FlatTableHeader>Absolute variance</FlatTableHeader><FlatTableHeader>Status</FlatTableHeader></FlatTableRow></FlatTableHead><FlatTableBody>{paginatedRuns.visibleRows.map((item) => <FlatTableRow key={item.id} onClick={() => onNavigate(`/reconciliation/${item.id}`)}><FlatTableCell><DateValue value={item.as_of_date} /></FlatTableCell><FlatTableCell>{new Date(item.created_at).toLocaleString()}</FlatTableCell><FlatTableCell>{item.item_count}</FlatTableCell><FlatTableCell>{item.exception_count}</FlatTableCell><FlatTableCell><FinancialAmount currency="USD" value={item.total_variance} /></FlatTableCell><FlatTableCell><StatusPill label={item.status} tone={item.status === "finalized" ? "positive" : "neutral"} /></FlatTableCell></FlatTableRow>)}</FlatTableBody></FlatTable>{paginatedRuns.pagination}</section>
  </div>;
}